import React, { useState, useEffect, useRef } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import RefreshIcon from '@mui/icons-material/Refresh';
import FeedIcon from '@mui/icons-material/Feed';
import CopyrightIcon from '@mui/icons-material/Copyright';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import '../shared.css';
import LoadingOverlay from '../LoadingOverlay';

function CutomerDetails() {
    const [dataItem, setDataItem] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchInput, setShowSearchInput] = useState(false);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const { isAuthenticated, companyId, branchId, permissions, token, logout } = useAuth();

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        const startDate = getFormattedStartDate();
        const currentDate = getFormattedCurrentDate();
        setFromDate(startDate);
        setToDate(currentDate);
    }, [isAuthenticated, companyId, branchId, token, logout]);

    useEffect(() => {
        if (fromDate && toDate) {
            fetchTableData(fromDate, toDate);
        }
    }, [fromDate, toDate]);

    const getFormattedCurrentDate = () => new Date().toISOString().split('T')[0];

    const getFormattedStartDate = () => {
        const d = new Date();
        d.setDate(1);
        return d.toISOString().split('T')[0];
    };

    const handleFromDateChange = (e) => setFromDate(e.target.value);
    const handleToDateChange = (e) => setToDate(e.target.value);

    const hasReadReportPermission = permissions.includes('Read Reports');
    const [loading, setLoading] = useState(false);
    const loadingRef = useRef(false);

    const fetchTableData = async (fDate, tDate) => {
        const effectiveFromDate = fDate || fromDate;
        const effectiveToDate = tDate || toDate;
        if (hasReadReportPermission && !loadingRef.current && effectiveFromDate && effectiveToDate) {
            setDataItem([]);
            loadingRef.current = true;
            setLoading(true);
            try {
                const response = await fetchData('ReportApi', `/CustomerDetails/${companyId}/${branchId}/${effectiveFromDate}/${effectiveToDate}`, token);
                if (response.responseCode === '1111') {
                    setDataItem(response.response_data);
                }
            } catch (error) {
                console.error('Error fetching Customer Details:', error);
            } finally {
                loadingRef.current = false;
                setLoading(false);
            }
        }
    };

    const handleSearch = (event) => setSearchQuery(event.target.value);

    const filteredDataItem = dataItem.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        return new Date(dateString).toLocaleDateString('en-GB', options);
    };

    const handleExportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Customer Data');

        const rightAlignColumns = [
            'Qty Billed', 'Total MRP Billed', 'Net Sales Value + Tax Billed', 'Total Discount %'
        ];

        const headerCaption = `Customer Data From : ${formatDate(fromDate)} To : ${formatDate(toDate)}`;
        worksheet.mergeCells('A1:N1');
        const headerCaptionCell = worksheet.getCell('A1');
        headerCaptionCell.value = headerCaption;
        headerCaptionCell.font = { bold: true, size: 14 };
        headerCaptionCell.alignment = { vertical: 'middle', horizontal: 'left' };

        const headerRowValues = [
            'Bill Number', 'Bill Date', 'Bill Time', 'Customer Name', 'Contact Number', 'Customer Address',
            'Qty Billed', 'Total MRP Billed', 'Net Sales Value + Tax Billed', 'Total Discount %',
            'Additional Disc Type', 'SC Membership Number', 'Voucher Number', 'Sales Man'
        ];
        const headerRow = worksheet.addRow(headerRowValues);
        headerRow.eachCell((cell) => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFB6D7A8' } };
            cell.font = { bold: true, color: { argb: 'FF000000' } };
            cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        });

        worksheet.columns = headerRowValues.map(() => ({ width: 18 }));

        filteredDataItem.forEach((rowData) => {
            const dataRow = [
                rowData.BillNumber, formatDate(rowData.BillDate), rowData.BillTime,
                rowData.CustomerName, rowData.ContactNumber, rowData.CustomerAddress,
                parseFloat(rowData.QtyBilled), parseFloat(rowData.MRPBilled),
                parseFloat(rowData.TotalBillAmt), parseFloat(rowData.TotalDiscPer),
                rowData.AdditionalDiscountType, rowData.SCMembershipNumber,
                rowData.VoucherNumber, rowData.SalesManName
            ];
            const row = worksheet.addRow(dataRow);
            row.eachCell((cell, colNumber) => {
                const headerName = headerRowValues[colNumber - 1];
                cell.alignment = { vertical: 'middle', horizontal: rightAlignColumns.includes(headerName) ? 'right' : 'center' };
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            });
        });

        const totalRowValues = [
            '', '', '', '', '', 'Total',
            parseFloat(totalQtyBilled), parseFloat(totalMRPBilled), parseFloat(totalTotalBillAmt),
            '', '', '', '', ''
        ];
        const totalRow = worksheet.addRow(totalRowValues);
        totalRow.font = { bold: true };
        totalRow.eachCell((cell, colNumber) => {
            const headerName = headerRowValues[colNumber - 1];
            cell.alignment = { vertical: 'middle', horizontal: rightAlignColumns.includes(headerName) ? 'right' : 'center' };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF2CC' } };
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        });

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), 'Customer_Data.xlsx');
    };

    const [totalQtyBilled, setTotalQtyBilled] = useState(0);
    const [totalMRPBilled, setTotalMRPBilled] = useState(0);
    const [totalTotalBillAmt, setTotalTotalBillAmt] = useState(0);

    useEffect(() => {
        let QtyBilled = 0, MRPBilled = 0, TotalBillAmt = 0;
        dataItem.forEach(item => {
            QtyBilled += Number(item?.QtyBilled ?? 0);
            MRPBilled += Number(item?.MRPBilled ?? 0);
            TotalBillAmt += Number(item?.TotalBillAmt ?? 0);
        });
        setTotalQtyBilled(QtyBilled.toFixed(2));
        setTotalMRPBilled(MRPBilled.toFixed(2));
        setTotalTotalBillAmt(TotalBillAmt.toFixed(2));
    }, [dataItem]);

    return (
        <div className="pg-container">
            <div className="pg-header">
                <h5 className="pg-title">Customer Data</h5>
                <div className="pg-header-actions">
                    <div className="pg-date-row">
                        <div className="pg-field">
                            <label className="pg-label" style={{ background: 'var(--pg-bg)' }}>From Date</label>
                            <input className="pg-input" type="date" value={fromDate} onChange={handleFromDateChange} />
                        </div>
                        <div className="pg-field">
                            <label className="pg-label" style={{ background: 'var(--pg-bg)' }}>To Date</label>
                            <input className="pg-input" type="date" value={toDate} onChange={handleToDateChange} />
                        </div>
                        <button className="pg-btn pg-btn-outline" onClick={() => fetchTableData()} disabled={loading}>
                            <RefreshIcon /> {loading ? 'Loading...' : 'Refresh'}
                        </button>
                        <button className="pg-btn pg-btn-success" onClick={handleExportExcel}>
                            <FeedIcon /> Excel
                        </button>
                        <button className="pg-btn pg-btn-outline" onClick={() => setShowSearchInput(v => !v)}>
                            <SearchIcon /> Search
                        </button>
                    </div>
                    {showSearchInput && (
                        <input className="pg-input pg-search-input" placeholder="Search…"
                            value={searchQuery} onChange={handleSearch} autoFocus style={{ marginTop: 6 }} />
                    )}
                </div>
            </div>

            {loading && <LoadingOverlay />}

            <div className="pg-table-wrap">
                <table className="pg-table">
                    <thead>
                        <tr>
                            <th>Bill Number</th>
                            <th>Bill Date</th>
                            <th>Bill Time</th>
                            <th>Customer Name</th>
                            <th>Contact Number</th>
                            <th>Customer Address</th>
                            <th>Qty Billed</th>
                            <th>Total MRP Billed</th>
                            <th>Net Sales Value + Tax Billed</th>
                            <th>Total Discount %</th>
                            <th>Addt'l. DISC Type</th>
                            <th>SC MembershipNumber</th>
                            <th>Voucher Number</th>
                            <th>Sales Man</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredDataItem.map((data, index) => (
                            <tr key={index}>
                                <td>{data.BillNumber}</td>
                                <td>{formatDate(data.BillDate)}</td>
                                <td>{data.BillTime}</td>
                                <td>{data.CustomerName}</td>
                                <td>{data.ContactNumber}</td>
                                <td>{data.CustomerAddress}</td>
                                <td style={{ textAlign: 'right' }}>{Number(data?.QtyBilled ?? 0).toFixed(2)}</td>
                                <td style={{ textAlign: 'right' }}>{Number(data?.MRPBilled ?? 0).toFixed(2)}</td>
                                <td style={{ textAlign: 'right' }}>{Number(data?.TotalBillAmt ?? 0).toFixed(2)}</td>
                                <td style={{ textAlign: 'right' }}>{Number(data?.TotalDiscPer ?? 0).toFixed(2)}</td>
                                <td>{data?.AdditionalDiscountType ?? ""}</td>
                                <td>{data.SCMembershipNumber}</td>
                                <td>{data.VoucherNumber}</td>
                                <td>{data.SalesManName}</td>
                            </tr>
                        ))}
                        <tr style={{ background: '#1c2a3a', color: '#fff', fontWeight: 700 }}>
                            <td colSpan={5}></td>
                            <td>Total Bills</td>
                            <td style={{ textAlign: 'right' }}>{totalQtyBilled}</td>
                            <td style={{ textAlign: 'right' }}>{totalMRPBilled}</td>
                            <td style={{ textAlign: 'right' }}>{totalTotalBillAmt}</td>
                            <td colSpan={5}></td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="pg-watermark"><CopyrightIcon style={{ fontSize: 12 }} /><b>EFFTABS SOLUTIONS LLP</b></div>
        </div>
    );
}

export default CutomerDetails;
